import React from 'react';
import { BOTTOM_INDENTS, COLORS } from '@constants';
import { useStore } from '@hooks';
import { Button, Tabs, Typography } from '@ui';
import { observer } from 'mobx-react';
import { useScriptTag } from '../../../../../../../../components/IntegrationKlickly/hooks/useScriptTag';
import { StyledSubtitle } from '../AnalyticsScriptModal/styled';
import { StyledCode, StyledDescription, StyledImportantNote, StyledText } from './styled';

const defaultPurchaseScript = `
<script>
    window.klpixel("event", "purchase", {
        shop: {
            checkout: {
                order_id: number, // REPLACE THIS WITH ACTUAL VALUE
                order_financial_status: "paid",
                order_created_at: new Date().toISOString(),
                order_tags: [string], // REPLACE THIS WITH ACTUAL VALUE
                email: string, // REPLACE THIS WITH ACTUAL VALUE
                phone: string, // REPLACE THIS WITH ACTUAL VALUE
                customer_id: number, // REPLACE THIS WITH ACTUAL VALUE
                line_items: [{
                    product_name: string, // REPLACE THIS WITH ACTUAL VALUE
                    product_id: number, // REPLACE THIS WITH ACTUAL VALUE
                    variant_id: number, // REPLACE THIS WITH ACTUAL VALUE
                    image: string, // REPLACE THIS WITH ACTUAL VALUE
                    price: number, // REPLACE THIS WITH ACTUAL VALUE
                    quantity: number // REPLACE THIS WITH ACTUAL VALUE
                }]
            }
        }
    });
</script>`.trim();

const defaultPurchaseScriptColored = `
<pre>
&lt;script&gt;
    window.klpixel("event", "purchase", {
        shop: {
            checkout: {
                order_id: <span>number</span>, // REPLACE THIS WITH ACTUAL VALUE
                order_financial_status: "paid",
                order_created_at: new Date().toISOString(),
                order_tags: <span>[string]</span>, // REPLACE THIS WITH ACTUAL VALUE
                email: <span>string</span>, // REPLACE THIS WITH ACTUAL VALUE
                phone: <span>string</span>, // REPLACE THIS WITH ACTUAL VALUE
                customer_id: <span>number</span>, // REPLACE THIS WITH ACTUAL VALUE
                line_items: [{
                    product_name: <span>string</span>, // REPLACE THIS WITH ACTUAL VALUE
                    product_id: <span>number</span>, // REPLACE THIS WITH ACTUAL VALUE
                    variant_id: <span>number</span>, // REPLACE THIS WITH ACTUAL VALUE
                    image: <span>string</span>, // REPLACE THIS WITH ACTUAL VALUE
                    price: <span>number</span>, // REPLACE THIS WITH ACTUAL VALUE
                    quantity: <span>number</span> // REPLACE THIS WITH ACTUAL VALUE
                }]
            }
        }
    });
&lt;/script&gt;
</pre>
`;

const AnalyticsScriptBlock = ({ analyticsScript, e2ePrefix }) => (
    <React.Fragment>
        <Typography.Text
            data-e2e={`${e2ePrefix}_analytics-script_text`}
            type='descriptor1'
            color={COLORS.$black}
            style={BOTTOM_INDENTS.XS}
        >
            Verify the analytics script from Step 1 is on your thank you page:
        </Typography.Text>
        <StyledCode data-e2e={`${e2ePrefix}_analytics-script_code`} style={BOTTOM_INDENTS.M}>
            <Button.Copy value={analyticsScript} />
            <code>{analyticsScript}</code>
        </StyledCode>
    </React.Fragment>
);

const PurchaseEventsModal = observer(() => {
    const store = useStore();
    const { isShopify, isBigCommerce, mainEcommerceShopDomain } = store.accountsStore;
    const { scriptTag } = useScriptTag();

    const addShopDomainToScriptTag = (scriptTagString) => {
        if (!scriptTagString || !mainEcommerceShopDomain) {
            return scriptTagString;
        }

        const urlMatch = scriptTagString.match(/src="([^"]+)"/);
        if (!urlMatch) {
            return scriptTagString;
        }

        const url = urlMatch[1];
        const urlObject = new URL(url);

        if (isShopify) {
            urlObject.searchParams.set('shop', mainEcommerceShopDomain);
        }

        return scriptTagString.replace(/src="[^"]+"/, `src="${urlObject.toString()}"`);
    };

    const analyticsScript = addShopDomainToScriptTag(scriptTag);

    const getCheckoutSubtitle = () => {
        if (isShopify) return 'For your checkout:';
        if (isBigCommerce) return 'For your standard Bigcommerce checkout:';
        return 'For your checkout:';
    };

    const shopifyTabs = [
        {
            key: 'shopify',
            label: <Typography.Text type='descriptor1'>Shopify based checkout (AfterSell, etc...)</Typography.Text>,
            children: (
                <React.Fragment>
                    <StyledText
                        data-e2e='integrations-purchase-modal-shopify_guide_text'
                        type='descriptor1'
                        color={COLORS.$black}
                        style={BOTTOM_INDENTS.M}
                    >
                        No additional purchase event script is needed. Our analytics script automatically captures
                        purchase data from Shopify&apos;s global order object (<code>window.Shopify.order</code>).
                        <ul>
                            <li>Verify the analytics script below is on your thank you page</li>
                            <li>Make a test purchase to confirm the script is working</li>
                            <li>Click &quot;Confirm connection&quot; to verify installation</li>
                        </ul>
                    </StyledText>
                    <AnalyticsScriptBlock
                        analyticsScript={analyticsScript}
                        e2ePrefix='integrations-purchase-modal-shopify'
                    />
                </React.Fragment>
            )
        },
        {
            key: 'custom',
            label: <Typography.Text type='descriptor1'>Custom checkout</Typography.Text>,
            children: (
                <React.Fragment>
                    <StyledImportantNote
                        data-e2e='integrations-purchase-modal-custom_important-note'
                        style={BOTTOM_INDENTS.M}
                    >
                        <Typography.Text align='left' type='descriptor1' color={COLORS.$tartRed}>
                            <strong>Important:</strong> Both scripts below are required on your custom checkout thank
                            you page. If you haven&apos;t set up the analytics script from Step 1, do that first.
                        </Typography.Text>
                    </StyledImportantNote>
                    <AnalyticsScriptBlock
                        analyticsScript={analyticsScript}
                        e2ePrefix='integrations-purchase-modal-custom'
                    />
                    <StyledText
                        data-e2e='integrations-purchase-modal-custom_guide_text'
                        type='descriptor1'
                        color={COLORS.$black}
                        style={BOTTOM_INDENTS.M}
                    >
                        Add the purchase event script below, replacing the highlighted placeholders with your actual
                        order data:
                    </StyledText>
                    <StyledCode data-e2e='integrations-purchase-modal-custom_code_text' style={BOTTOM_INDENTS.XS}>
                        <Button.Copy value={defaultPurchaseScript} />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: defaultPurchaseScriptColored
                            }}
                        />
                    </StyledCode>
                    <StyledText type='descriptor1' color={COLORS.$black} style={BOTTOM_INDENTS.M}>
                        <ul>
                            <li>Make a test purchase to confirm the script is working</li>
                            <li>Click &quot;Confirm connection&quot; to verify installation</li>
                        </ul>
                    </StyledText>
                </React.Fragment>
            )
        }
    ];

    return (
        <React.Fragment>
            <Typography.Text
                data-e2e='integrations-purchase-modal_description_text'
                type='descriptor1'
                color={COLORS.$black}
                style={BOTTOM_INDENTS.M}
            >
                In order to scale your marketing campaigns and accurately charge commissions, Klickly&apos;s Conversion
                Script needs the ability to analyze your purchase events.
            </Typography.Text>
            <StyledSubtitle>{getCheckoutSubtitle()}</StyledSubtitle>
            {isShopify ? (
                <Tabs fullWidth centered defaultActiveKey='shopify' items={shopifyTabs} destroyInactiveTabPane />
            ) : (
                <React.Fragment>
                    <StyledImportantNote data-e2e='integrations-purchase-modal_important-note' style={BOTTOM_INDENTS.M}>
                        <Typography.Text align='left' type='descriptor1' color={COLORS.$tartRed}>
                            <strong>Important:</strong> Both scripts below are required on your checkout thank you page.
                            If you haven&apos;t set up the analytics script from Step 1, do that first.
                        </Typography.Text>
                    </StyledImportantNote>
                    <AnalyticsScriptBlock analyticsScript={analyticsScript} e2ePrefix='integrations-purchase-modal' />
                    <StyledText
                        data-e2e='integrations-purchase-modal_guide_text'
                        type='descriptor1'
                        color={COLORS.$black}
                        style={BOTTOM_INDENTS.M}
                    >
                        Add the purchase event script below, replacing the highlighted placeholders with your actual
                        order data:
                    </StyledText>
                    <StyledCode data-e2e='integrations-purchase-modal_code_text' style={BOTTOM_INDENTS.XS}>
                        <Button.Copy value={defaultPurchaseScript} />
                        <div
                            dangerouslySetInnerHTML={{
                                __html: defaultPurchaseScriptColored
                            }}
                        />
                    </StyledCode>
                    <StyledText type='descriptor1' color={COLORS.$black} style={BOTTOM_INDENTS.M}>
                        <ul>
                            <li>Make a test purchase to confirm the script is working</li>
                            <li>Click &quot;Confirm connection&quot; to verify installation</li>
                        </ul>
                    </StyledText>
                </React.Fragment>
            )}
            <StyledDescription>
                <Typography.Text data-e2e='integrations-purchase-modal_warning_text' align='left' type='descriptor1'>
                    Once correctly installed, the status of this step will automatically change to Connected.
                </Typography.Text>
            </StyledDescription>
        </React.Fragment>
    );
});

export default PurchaseEventsModal;
